import React, { useRef } from 'react'
import classNames from 'classnames'

import { usePortal } from '@/utils/hooks/usePortal'
import { useEscapeKey } from '@/utils/hooks/useEscapeKey'
import { useBodyScrollLock } from '@/utils/hooks/useBodyScrollLock'

import './Modal.scss'

/**
 * @import import { Modal } from '@/components/layout/modals/Modal'
 * @param {object} props
 * @param {node} props.children Dialog content
 * @param {Function} [props.onClose] Called on Escape or an overlay click
 * @param {'tiny'|'small'|'large'|'xlarge'} [props.size] Width preset; omit for the default width
 * @param {string} [props.className] Extra class names for the dialog element
 * @param {boolean} [props.closeOnEscape] Set false to keep Escape from closing the dialog
 * @param {boolean} [props.canCloseOnDimmerClick] Set false to keep overlay clicks from closing the dialog
 */
export function Modal({
  children,
  onClose,
  size,
  className,
  closeOnEscape = true,
  canCloseOnDimmerClick = true,
  ...props
}) {
  const portal = usePortal()
  // A click only closes the modal if it both started and ended on the
  // overlay — dragging a text selection out of the modal must not dismiss it.
  const mouseDownOnOverlay = useRef(false)

  useEscapeKey(onClose, closeOnEscape)
  useBodyScrollLock()

  const handleOverlayMouseDown = (event) => {
    mouseDownOnOverlay.current = event.target === event.currentTarget
  }

  const handleOverlayClick = (event) => {
    if (
      canCloseOnDimmerClick &&
      mouseDownOnOverlay.current &&
      event.target === event.currentTarget
    ) {
      onClose?.()
    }
  }

  return portal(
    <div
      className="modal-overlay"
      data-testid="modal-overlay"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={classNames('modal', size, className)}
        {...props}
      >
        {children}
      </div>
    </div>
  )
}
